/**
 * <shipping-info-component>
 *
 * Renders an estimated-delivery line ("Est. Delivery to 4220: 2 Apr - 6 Apr  Update")
 * and an inline "DELIVERY LOCATION" panel (sits directly under the estimate line) that
 * lets the customer search the bundled Australia Post postcode/suburb list
 * (assets/au-postcodes.json), pick a suggestion, and confirm it with the "Check"
 * button.
 *
 * Data attributes (set from snippets/shipping-info.liquid):
 *   data-postcode       Initial postcode (from the logged-in customer's default address).
 *   data-postcodes-url  asset_url of au-postcodes.json (lazy-loaded on first open).
 *   data-geocode-url    Optional endpoint for "use my current location" (lat/lng -> postcode).
 *   data-min-days       Min business days for the estimate (default 2).
 *   data-max-days       Max business days for the estimate (default 6).
 */
if (!customElements.get("shipping-info-component")) {
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const STORAGE_KEY = "delivery_postcode";
  const MAX_RESULTS = 12;

  class ShippingInfoComponent extends HTMLElement {
    connectedCallback() {
      this.minDays = parseInt(this.dataset.minDays || "2", 10);
      this.maxDays = parseInt(this.dataset.maxDays || "6", 10);
      this.postcodesUrl = this.dataset.postcodesUrl || "";
      this.geocodeUrl = this.dataset.geocodeUrl || "";
      this.estimateUrl = this.dataset.estimateUrl || "";
      this.estimateToken = this.dataset.estimateToken || "";

      this.estimate = this.querySelector(".shipping-info__estimate");
      this.drawer = this.querySelector("[data-drawer]");
      this.input = this.querySelector("[data-search-input]");
      this.checkBtn = this.querySelector("[data-check]");
      this.results = this.querySelector("[data-results]");
      this.message = this.querySelector("[data-message]");
      this.setView = this.querySelector("[data-set]");
      this.unsetView = this.querySelector("[data-unset]");
      this.postcodeEls = this.querySelectorAll("[data-postcode-value]");
      this.datesEls = this.querySelectorAll("[data-dates]");

      this._data = null;
      this._loadingData = null;
      // ADDED: holds the postcode/suburb/state the customer picked from the
      // results list until they confirm it with the Check button.
      this._pending = null;

      this.querySelectorAll("[data-open]").forEach((el) =>
        el.addEventListener("click", (e) => {
          e.preventDefault();
          this.open();
        }),
      );
      this.querySelectorAll("[data-close]").forEach((el) =>
        el.addEventListener("click", (e) => {
          e.preventDefault();
          this.close();
        }),
      );

      this.input.addEventListener(
        "input",
        this._debounce(() => this.search(), 150),
      );
      // ADDED: typing after picking a suggestion invalidates that pick —
      // Check must not silently apply a stale selection.
      this.input.addEventListener("input", () => {
        this._pending = null;
      });

      // CHANGED: use "mousedown" (not "click") to pick a suggestion, and
      // preventDefault it. On click, focus moves input -> button first,
      // which can fire a native "blur"/"focusout" ahead of the click and,
      // on some themes, that blur/outside-click handling removes or
      // re-renders the results list before the click event ever reaches it
      // — so the click silently does nothing. mousedown fires first and
      // preventDefault keeps focus on the input, avoiding that race
      // entirely. stopPropagation keeps this from also being read as an
      // "outside click" or bubbling into an ancestor accordion/disclosure.
      this.results.addEventListener("mousedown", (e) => {
        const item = e.target.closest("[data-pc]");
        if (!item) return;
        e.preventDefault();
        e.stopPropagation();
        this._pickSuggestion(
          item.dataset.pc,
          item.dataset.loc,
          item.dataset.state,
        );
      });

      // ADDED: "Check" button confirms the picked suggestion.
      if (this.checkBtn)
        this.checkBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.checkSelection();
        });

      const geo = this.querySelector("[data-geolocate]");
      if (geo)
        geo.addEventListener("click", (e) => {
          e.preventDefault();
          this.useCurrentLocation();
        });

      // CHANGED: drawer is now a plain <div>, not a <dialog>, so there is no
      // ::backdrop to click and no native 'cancel' event (Esc key) to catch.
      // Old dialog-only listeners commented out below.
      //
      // this.drawer.addEventListener('click', (e) => {
      //   if (e.target === this.drawer) this.close();
      // });
      // this.drawer.addEventListener('cancel', (e) => {
      //   e.preventDefault();
      //   this.close();
      // });

      // ADDED: since the panel is inline now (no backdrop), close it if the
      // customer clicks anywhere outside the component while it's open.
      document.addEventListener("click", (e) => {
        if (!this.drawer.hidden && !this.contains(e.target)) this.close();
      });

      const saved = this._readStored();
      const isLoggedIn = this.dataset.customerLoggedIn === "true";
      let initialPostcode = "";
      let initialSuburb = "";
      let initialState = "";

      if (isLoggedIn) {
        initialPostcode = this.dataset.postcode || saved.postcode || "";
        initialSuburb = this.dataset.suburb || saved.suburb || "";
        initialState = this.dataset.state || saved.state || "";
      } else {
        initialPostcode = saved.postcode || this.dataset.postcode || "";
        initialSuburb = saved.suburb || this.dataset.suburb || "";
        initialState = saved.state || this.dataset.state || "";
      }

      this.setPostcode(initialPostcode, initialSuburb, initialState, false);
    }

    /* ----------------------------------------------------------- delivery line */

    async setPostcode(postcode, suburb, state, persist) {
      postcode = (postcode || "").trim();
      suburb = (suburb || "").trim();
      state = (state || "").trim();
      this.postcode = postcode;
      this.suburb = suburb;
      this.state = state;

      if (postcode) {
        this.postcodeEls.forEach((el) => (el.textContent = postcode));
        if (this.setView) this.setView.hidden = false;
        if (this.unsetView) this.unsetView.hidden = true;

        if (this.estimateUrl) {
          this.datesEls.forEach((el) => (el.textContent = "Calculating..."));
          try {
            const range = await this.fetchEstimateFromDataJet(
              postcode,
              suburb,
              state,
            );
            this.datesEls.forEach((el) => (el.textContent = range));
          } catch (e) {
            console.error(
              "[shipping-info] DataJet estimate failed, falling back to local:",
              e,
            );
            const range = this._estimateRange();
            this.datesEls.forEach((el) => (el.textContent = range));
          }
        } else {
          const range = this._estimateRange();
          this.datesEls.forEach((el) => (el.textContent = range));
        }
      } else {
        if (this.setView) this.setView.hidden = true;
        if (this.unsetView) this.unsetView.hidden = false;
      }

      if (persist && postcode) {
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ postcode, suburb, state }),
          );
        } catch (e) {
          /* storage unavailable */
        }
      }
    }

    _estimateRange(min = this.minDays, max = this.maxDays) {
      const start = this._addBusinessDays(new Date(), min);
      const end = this._addBusinessDays(new Date(), max);
      return this._fmt(start) + " - " + this._fmt(end);
    }

    _addBusinessDays(date, days) {
      const d = new Date(date.getTime());
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    }

    _fmt(d) {
      return d.getDate() + " " + MONTHS[d.getMonth()];
    }

    /* ------------------------------------------------------------ inline panel */

    open() {
      this._setMessage("");
      this.input.classList.remove("is-invalid");
      this.input.value = "";
      this.results.innerHTML = "";
      this._pending = null;

      // CHANGED: was dialog.showModal() / setAttribute('open'). Panel is now a
      // plain <div> inline in the page, so we just unhide it.
      // if (typeof this.drawer.showModal === 'function') {
      //   this.drawer.showModal();
      // } else {
      //   this.drawer.setAttribute('open', '');
      // }
      this.drawer.hidden = false;

      // ADDED: hide the "Est. Delivery to..." line while the panel is open.
      if (this.estimate) this.estimate.hidden = true;

      this.loadData();

      // CHANGED: body scroll lock is no longer needed — the panel doesn't
      // cover the page like the old fixed drawer did.
      // document.body.classList.add('shipping-info-open');

      this.input.focus();
    }

    close() {
      // CHANGED: was dialog.close() plus a slide-out animation on 'animationend'.
      // Panel is now a plain <div>, so we just hide it immediately.
      // this.drawer.classList.add('is-closing');
      // const finish = () => {
      //   this.drawer.classList.remove('is-closing');
      //   if (typeof this.drawer.close === 'function') {
      //     this.drawer.close();
      //   } else {
      //     this.drawer.removeAttribute('open');
      //   }
      // };
      // this.drawer.addEventListener('animationend', finish, { once: true });
      // document.body.classList.remove('shipping-info-open');
      // setTimeout(finish, 320);

      this.drawer.hidden = true;

      // ADDED: bring the "Est. Delivery to..." line back once the panel closes.
      if (this.estimate) this.estimate.hidden = false;
    }

    /* ------------------------------------------------------------ data + search */

    loadData() {
      if (this._data || this._loadingData)
        return this._loadingData || Promise.resolve();
      this._loadingData = fetch(this.postcodesUrl)
        .then((r) => {
          if (!r.ok) throw new Error("Bad response");
          return r.json();
        })
        .then((rows) => {
          this._data = rows;
          return rows;
        })
        .catch(() => {
          this._data = null;
          this._loadingData = null;
          this._showApiError();
        });
      return this._loadingData;
    }

    async search() {
      const q = this.input.value.trim();
      this.input.classList.remove("is-invalid");
      if (q.length < 2) {
        this.results.innerHTML = "";
        this._setMessage("");
        return;
      }

      if (!this._data) await this.loadData();
      if (!this._data) return; // load failed -> API error already shown

      const matches = this._match(q);
      if (matches.length) {
        this._setMessage("");
        this.renderResults(matches);
      } else {
        this.results.innerHTML = "";
        this.input.classList.add("is-invalid");
        const numeric = /^\d+$/.test(q);
        this._setMessage(
          numeric ? "Please enter a valid postcode." : "No suggestions found",
          "invalid",
        );
      }
    }

    _match(q) {
      const lc = q.toLowerCase();
      if (/^\d+$/.test(q)) {
        const qn = parseInt(q, 10);
        return this._data
          .filter(
            (r) =>
              r[0].startsWith(q) || Math.abs(parseInt(r[0], 10) - qn) <= 15,
          )
          .sort((a, b) => {
            // Exact prefix matches first, then nearest postcode.
            const ap = a[0].startsWith(q) ? 0 : 1;
            const bp = b[0].startsWith(q) ? 0 : 1;
            if (ap !== bp) return ap - bp;
            return (
              Math.abs(parseInt(a[0], 10) - qn) -
              Math.abs(parseInt(b[0], 10) - qn)
            );
          })
          .slice(0, MAX_RESULTS);
      }
      const starts = [];
      const includes = [];
      for (const r of this._data) {
        const loc = r[1].toLowerCase();
        if (loc.startsWith(lc)) starts.push(r);
        else if (loc.includes(lc)) includes.push(r);
        if (starts.length >= MAX_RESULTS) break;
      }
      return starts.concat(includes).slice(0, MAX_RESULTS);
    }

    renderResults(matches) {
      this.results.innerHTML = matches
        .map(
          (r) =>
            '<li><button type="button" class="shipping-info__result" data-pc="' +
            r[0] +
            '" data-loc="' +
            this._esc(r[1]) +
            '" data-state="' +
            this._esc(r[2] || "") +
            '"><span class="shipping-info__result-pc">' +
            r[0] +
            '</span> <span class="shipping-info__result-loc">' +
            this._esc(r[1]) +
            "</span></button></li>",
        )
        .join("");
    }

    /**
     * ADDED: called when the customer clicks a suggestion in the results
     * list. This only fills the input and remembers the pick — it does NOT
     * apply the postcode. Applying happens in checkSelection(), once the
     * customer clicks the "Check" button.
     */
    _pickSuggestion(postcode, suburb = "", state = "") {
      this._pending = { postcode, suburb, state };
      const label = [suburb, state].filter(Boolean).join(", ");
      this.input.value = label ? label + " " + postcode : postcode;
      this.input.classList.remove("is-invalid");
      this.results.innerHTML = "";
      this._setMessage("");
    }

    /**
     * ADDED: fired by the "Check" button. Applies the pending suggestion
     * (updates the estimate + persists it), or prompts the customer to pick
     * one first if nothing was selected.
     */
    checkSelection() {
      if (this._pending) {
        this.select(
          this._pending.postcode,
          this._pending.suburb,
          this._pending.state,
        );
        this._pending = null;
        return;
      }
      this.input.classList.add("is-invalid");
      this._setMessage(
        "Please select a suburb or postcode from the list.",
        "invalid",
      );
    }

    select(postcode, suburb = "", state = "") {
      this.setPostcode(postcode, suburb, state, true);
      this.close();
    }

    async fetchEstimateFromDataJet(postcode, suburb, state) {
      const headers = { "Content-Type": "application/json" };
      if (this.estimateToken) {
        headers["token"] = this.estimateToken;
      }

      const res = await fetch(this.estimateUrl, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          postcode: postcode || "",
          suburb: suburb || "",
          state: state || "",
        }),
      });

      if (!res.ok) {
        throw new Error("DataJet task estimate failed");
      }

      const data = await res.json();

      // Case 1: Pre-formatted date range returned (e.g. "12 Jul - 15 Jul")
      const dates = data.dates || data.estimate || data.estimated_delivery;
      if (dates) return dates;

      // Case 2: Min and Max days returned (e.g. { min_days: 2, max_days: 5 })
      const min = parseInt(
        data.min_days || data.minDays || data.min_delivery_days,
        10,
      );
      const max = parseInt(
        data.max_days || data.maxDays || data.max_delivery_days,
        10,
      );
      if (!isNaN(min) && !isNaN(max)) {
        return this._estimateRange(min, max);
      }

      throw new Error("Estimated dates/days not found in response");
    }

    /* ----------------------------------------------------- use current location */

    useCurrentLocation() {
      if (!("geolocation" in navigator)) {
        this._showApiError();
        return;
      }
      this._setMessage("Detecting your location…", "loading");
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          this._resolveLocation(pos.coords.latitude, pos.coords.longitude),
        () => this._showApiError(),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
      );
    }

    _resolveLocation(lat, lng) {
      // A reverse-geocode endpoint is required to turn coordinates into a postcode.
      // When none is configured we surface the API-issue state.
      if (!this.geocodeUrl) {
        this._showApiError();
        return;
      }
      const url =
        this.geocodeUrl +
        (this.geocodeUrl.includes("?") ? "&" : "?") +
        "lat=" +
        lat +
        "&lng=" +
        lng;
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error("Bad response");
          return r.json();
        })
        .then((data) => {
          const pc = data && (data.postcode || data.zip);
          if (pc) {
            this.select(String(pc));
          } else {
            this._showApiError();
          }
        })
        .catch(() => this._showApiError());
    }

    _showApiError() {
      this.results.innerHTML = "";
      this._setMessage(
        "We're having trouble loading delivery locations right now. Please try again later.",
        "error",
      );
    }

    /* ----------------------------------------------------------------- helpers */

    _setMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text || "";
      this.message.hidden = !text;
      this.message.className =
        "shipping-info__message" +
        (type ? " shipping-info__message--" + type : "");
    }

    _readStored() {
      try {
        const val = localStorage.getItem(STORAGE_KEY) || "";
        if (val && val.startsWith("{")) {
          return JSON.parse(val);
        }
        return { postcode: val, suburb: "", state: "" };
      } catch (e) {
        return { postcode: "", suburb: "", state: "" };
      }
    }

    _esc(s) {
      return String(s).replace(
        /[&<>"']/g,
        (c) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[c],
      );
    }

    _debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define("shipping-info-component", ShippingInfoComponent);
}
